import moment from 'moment';
import {
    BUREAU_NIVEL_ADMINISTRADOR,
    BUREAU_TAMANHO_PAGINA,
    IBureau,
    IBureauDetalhe,
    IBureauMembro,
    IBureauTenant,
    IBureauTenantResumo,
    IBureauUsuario,
} from '../models/bureaus.model';
import {
    ABA_ADMINISTRADORES_EQUIPE,
    ABA_BUREAUS,
    ABA_MEMBROS_EQUIPE,
    BUREAU_PERMISSAO_ADMINISTRADOR,
    BUREAU_PERMISSAO_TENANTS_ADMINISTRADOR,
} from '../models/bureaus-equipes.model';
import { MeurhBureausEquipesModalService } from '../equipes/modal/bureaus-equipes.modal.service';
import { MeurhBureausEquipesService } from '../bureaus-equipes.service';
import { MeurhBureausModalService } from '../modal/bureaus.modal.service';
import { MeurhBureausService } from '../bureaus.service';
import { ModalConfirmService } from '../../../../shared/components/modal-confirm/modal.confirm.service';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export class MeurhBureausIndexController {
    static $inject = [
        '$rootScope',
        'MeurhBureausModalService',
        'MeurhBureausService',
        'MeurhBureausEquipesModalService',
        'MeurhBureausEquipesService',
        'ModalConfirmService',
        'NewToaster',
    ];

    public readonly abaBureaus = ABA_BUREAUS;
    public readonly abaAdministradoresEquipe = ABA_ADMINISTRADORES_EQUIPE;
    public readonly abaMembrosEquipe = ABA_MEMBROS_EQUIPE;

    public bureaus: IBureau[] = [];
    /** Total da lista, do `full_count` da primeira página. */
    public totalBureausLista = 0;
    /** A última página veio incompleta: não há mais Bureaus a carregar. */
    public fimBureaus = true;
    public carregandoMaisBureaus = false;
    public detalhes: Record<string, IBureauDetalhe> = {};
    public filtro = '';
    public semPermissao = false;
    public ehAdministradorBureaus = false;
    public ehAdministradorEquipe = false;
    public abaAtiva: number;
    /** Tenants que o administrador de equipe administra. Vazio para o administrador de Bureaus. */
    public tenantsLimite: IBureauTenantResumo[] = [];
    /** Incrementados depois de uma inclusão pelo botão do título, para a aba recarregar a lista. */
    public recargaAdministradores = 0;
    public recargaMembros = 0;

    constructor(
        private $rootScope: any,
        private meurhBureausModalService: MeurhBureausModalService,
        public meurhBureausService: MeurhBureausService,
        private meurhBureausEquipesModalService: MeurhBureausEquipesModalService,
        public meurhBureausEquipesService: MeurhBureausEquipesService,
        private modalConfirmService: ModalConfirmService,
        private NewToaster: any,
    ) {
        this.ehAdministradorBureaus = this.$rootScope.temPermissao(BUREAU_PERMISSAO_ADMINISTRADOR) === true;
        this.ehAdministradorEquipe = this.$rootScope.temPermissao(BUREAU_PERMISSAO_TENANTS_ADMINISTRADOR) === true;

        if (!this.ehAdministradorBureaus && !this.ehAdministradorEquipe) {
            this.semPermissao = true;
            return;
        }

        if (this.ehAdministradorBureaus) {
            this.abaAtiva = ABA_BUREAUS;
            this.carregarBureaus();
            return;
        }

        this.abaAtiva = ABA_MEMBROS_EQUIPE;
        this.carregarTenantsLimite();
    }

    public adicionarAdministradorEquipe(): void {
        const modal = this.meurhBureausEquipesModalService.open({
            tipo: 'administrador',
            tenantPorId: true,
            tenantsLimite: [],
        });

        modal.result
            .then(() => {
                this.recargaAdministradores++;
            })
            .catch(() => undefined);
    }

    public adicionarMembroEquipe(): void {
        const modal = this.meurhBureausEquipesModalService.open({
            tipo: 'membro',
            tenantPorId: this.ehAdministradorBureaus,
            tenantsLimite: this.tenantsLimite,
        });

        modal.result
            .then(() => {
                this.recargaMembros++;
            })
            .catch(() => undefined);
    }

    public carregarBureaus(filtro: string = ''): void {
        this.carregandoMaisBureaus = false;

        this.meurhBureausService
            .getList(filtro)
            .then((bureaus) => {
                this.semPermissao = false;
                this.bureaus = bureaus || [];
                this.totalBureausLista = this.bureaus.length > 0 ? this.bureaus[0].full_count || 0 : 0;
                this.fimBureaus = this.bureaus.length < BUREAU_TAMANHO_PAGINA;
            })
            .catch((error) => {
                this.tratarErroBureaus(error);
            });
    }

    public carregarMaisBureaus(): void {
        const ultimo = this.bureaus[this.bureaus.length - 1];
        this.carregandoMaisBureaus = true;

        this.meurhBureausService
            .getList(this.filtro, { created_at: ultimo.created_at, bureau: ultimo.bureau })
            .then((bureaus) => {
                this.bureaus = this.bureaus.concat(bureaus || []);
                this.fimBureaus = (bureaus || []).length < BUREAU_TAMANHO_PAGINA;
            })
            .catch((error) => {
                this.tratarErroBureaus(error);
            })
            .finally(() => {
                this.carregandoMaisBureaus = false;
            });
    }

    public buscar(): void {
        this.carregarBureaus(this.filtro);
    }

    public totalBureaus(): number {
        return this.totalBureausLista;
    }

    /** As abas de equipe avisam quando a API responde 403: a tela passa a "sem permissão". */
    public marcarSemPermissao(): void {
        this.semPermissao = true;
    }

    /** Cria a entrada de detalhe do bureau na primeira vez que ela é pedida. */
    public detalhe(bureau: IBureau): IBureauDetalhe {
        if (!this.detalhes[bureau.bureau]) {
            this.detalhes[bureau.bureau] = {
                tenants: [],
                membros: [],
                tenantsCarregados: false,
                membrosCarregados: false,
                tenantsBusy: false,
                membrosBusy: false,
                novoTenantId: null,
                novoMembroEmail: '',
                adicionandoTenant: false,
                adicionandoMembro: false,
            };
        }

        return this.detalhes[bureau.bureau];
    }

    /**
     * `created_at` chega em UTC, como timestamp sem offset
     * (`2026-07-07 22:52:01.918386`), que o filtro `date` do Angular não interpreta.
     * Os filtros da casa (`asDateTime`, `amDateFormat`) leem a string como horário
     * local e só definem o fuso de destino, então não corrigem o deslocamento:
     * é preciso interpretar como UTC e converter para o fuso do usuário.
     */
    public dataHoraCriacao(bureau: IBureau): string {
        if (!bureau.created_at) {
            return '';
        }

        const criacao = moment.utc(bureau.created_at, 'YYYY-MM-DD HH:mm:ss');

        return criacao.isValid() ? criacao.local().format('DD/MM/YYYY HH:mm:ss') : '';
    }

    public rotuloContagemTenants(bureau: IBureau): string {
        const detalhe = this.detalhe(bureau);

        return detalhe.tenantsCarregados ? ` (${detalhe.tenants.length})` : '';
    }

    public rotuloContagemMembros(bureau: IBureau): string {
        const detalhe = this.detalhe(bureau);

        return detalhe.membrosCarregados ? ` (${detalhe.membros.length})` : '';
    }

    /** Rótulo do tenant: "<id> · <codigo>" e, se houver, "· <nome>" no fim. */
    public rotuloTenant(vinculo: IBureauTenant): string {
        const tenant = vinculo.tenant;
        const base = `${tenant.tenant} · ${tenant.codigo}`;
        const nome = (tenant.nome || '').trim();

        return nome ? `${base} · ${nome}` : base;
    }

    /** Nome do membro com o sobrenome só quando existir de fato. */
    public nomeMembro(usuario: IBureauUsuario): string {
        const nome = (usuario.nome || '').trim();
        const sobrenome = (usuario.sobrenome || '').trim();

        return sobrenome ? `${nome} ${sobrenome}` : nome;
    }

    public rotuloNivel(nivel: number): string {
        return nivel === BUREAU_NIVEL_ADMINISTRADOR ? 'Administrador' : 'Membro';
    }

    public garantirTenants(bureau: IBureau): void {
        const detalhe = this.detalhe(bureau);

        if (detalhe.tenantsCarregados || detalhe.tenantsBusy) {
            return;
        }

        this.recarregarTenants(bureau);
    }

    public garantirMembros(bureau: IBureau): void {
        const detalhe = this.detalhe(bureau);

        if (detalhe.membrosCarregados || detalhe.membrosBusy) {
            return;
        }

        this.recarregarMembros(bureau);
    }

    public tenantIdValido(bureau: IBureau): boolean {
        return this.tenantIdInformado(bureau) !== null;
    }

    public emailMembroValido(bureau: IBureau): boolean {
        const email = (this.detalhe(bureau).novoMembroEmail || '').trim();

        return EMAIL_REGEX.test(email);
    }

    public criarBureau(): void {
        const modal = this.meurhBureausModalService.open({
            mode: 'create',
            bureau: {
                nome: '',
            },
        });

        modal.result
            .then(() => {
                this.carregarBureaus(this.filtro);
            })
            .catch(() => undefined);
    }

    public editarBureau(bureau: IBureau): void {
        const modal = this.meurhBureausModalService.open({
            mode: 'edit',
            bureau: {
                bureau: bureau.bureau,
                nome: bureau.nome,
            },
        });

        modal.result
            .then(() => {
                this.carregarBureaus(this.filtro);
            })
            .catch(() => undefined);
    }

    public excluirBureau(bureau: IBureau): void {
        const modal = this.modalConfirmService.open(
            bureau,
            'bureau',
            true,
            {
                title: 'Excluir bureau',
                subtitle: `Você está prestes a excluir ${bureau.nome}.`,
                confirmText: 'Ao excluir, os vínculos de tenants e membros deste bureau deixam de valer. Tem certeza que deseja excluir?',
                closeButton: 'Cancelar',
                confirmButton: 'Sim, excluir',
            },
            true,
        );

        modal.result
            .then(() => {
                this.executarExclusaoBureau(bureau);
            })
            .catch(() => undefined);
    }

    public adicionarTenant(bureau: IBureau): void {
        const tenantId = this.tenantIdInformado(bureau);

        if (tenantId === null) {
            this.NewToaster.pop({
                type: 'error',
                title: 'Erro ao vincular tenant',
                body: 'Informe um ID de tenant válido.'
            });
            return;
        }

        const detalhe = this.detalhe(bureau);
        detalhe.adicionandoTenant = true;

        this.meurhBureausService
            .createTenant(bureau.bureau, tenantId)
            .then(() => {
                this.NewToaster.pop({
                    type: 'success',
                    title: 'Tenant vinculado com sucesso!'
                });

                detalhe.novoTenantId = null;
                this.recarregarTenants(bureau);
            })
            .catch((error) => {
                this.NewToaster.pop({
                    type: 'error',
                    title: 'Erro ao vincular tenant',
                    body: error?.data?.message || 'Ocorreu um erro ao vincular o tenant ao bureau.'
                });
            })
            .finally(() => {
                detalhe.adicionandoTenant = false;
            });
    }

    public removerTenant(bureau: IBureau, tenant: IBureauTenant): void {
        const modal = this.modalConfirmService.open(
            tenant,
            'tenant',
            true,
            {
                title: 'Remover tenant do bureau',
                subtitle: `Você está prestes a remover o tenant ${this.rotuloTenant(tenant)} de ${bureau.nome}.`,
                confirmText: 'Tem certeza que deseja remover este tenant do bureau?',
                closeButton: 'Cancelar',
                confirmButton: 'Sim, remover',
            },
            true,
        );

        modal.result
            .then(() => {
                this.executarRemocaoTenant(bureau, tenant);
            })
            .catch(() => undefined);
    }

    public adicionarMembro(bureau: IBureau): void {
        const detalhe = this.detalhe(bureau);
        const email = (detalhe.novoMembroEmail || '').trim();

        if (!EMAIL_REGEX.test(email)) {
            this.NewToaster.pop({
                type: 'error',
                title: 'Erro ao adicionar membro',
                body: 'Informe um e-mail válido.'
            });
            return;
        }

        detalhe.adicionandoMembro = true;

        this.meurhBureausService
            .createMembro(bureau.bureau, email)
            .then(() => {
                this.NewToaster.pop({
                    type: 'success',
                    title: 'Membro adicionado com sucesso!'
                });

                detalhe.novoMembroEmail = '';
                this.recarregarMembros(bureau);
            })
            .catch((error) => {
                this.NewToaster.pop({
                    type: 'error',
                    title: 'Erro ao adicionar membro',
                    body: error?.data?.message || 'Ocorreu um erro ao adicionar o membro ao bureau.'
                });
            })
            .finally(() => {
                detalhe.adicionandoMembro = false;
            });
    }

    public removerMembro(bureau: IBureau, membro: IBureauMembro): void {
        const modal = this.modalConfirmService.open(
            membro,
            'membro',
            true,
            {
                title: 'Remover membro do bureau',
                subtitle: `Você está prestes a remover ${this.nomeMembro(membro.bureauusuario)} de ${bureau.nome}.`,
                confirmText: 'Tem certeza que deseja remover este membro do bureau?',
                closeButton: 'Cancelar',
                confirmButton: 'Sim, remover',
            },
            true,
        );

        modal.result
            .then(() => {
                this.executarRemocaoMembro(bureau, membro);
            })
            .catch(() => undefined);
    }

    private carregarTenantsLimite(): void {
        this.meurhBureausEquipesService
            .getMeusTenants()
            .then((tenants) => {
                this.tenantsLimite = tenants || [];
            })
            .catch((error) => {
                // permissão retirada depois do login: o menu ainda mostra a tela até recarregar a página
                if (error?.status === 403) {
                    this.semPermissao = true;
                    return;
                }

                this.NewToaster.pop({
                    type: 'error',
                    title: 'Erro ao carregar tenants',
                    body: error?.data?.message || 'Ocorreu um erro ao carregar os tenants que você administra.'
                });
            });
    }

    private tratarErroBureaus(error: any): void {
        if (this.requisicaoCancelada(error)) {
            return;
        }

        if (error?.status === 403) {
            this.semPermissao = true;
            this.bureaus = [];
            return;
        }

        this.NewToaster.pop({
            type: 'error',
            title: 'Erro ao carregar bureaus',
            body: error?.data?.message || 'Ocorreu um erro ao carregar os bureaus.'
        });
    }

    private executarExclusaoBureau(bureau: IBureau): void {
        this.meurhBureausService
            .delete(bureau.bureau)
            .then(() => {
                this.NewToaster.pop({
                    type: 'success',
                    title: 'Bureau excluído com sucesso!'
                });

                delete this.detalhes[bureau.bureau];
                this.carregarBureaus(this.filtro);
            })
            .catch((error) => {
                this.NewToaster.pop({
                    type: 'error',
                    title: 'Erro ao excluir bureau',
                    body: error?.data?.message || 'Ocorreu um erro ao tentar excluir o bureau.'
                });
            });
    }

    private executarRemocaoTenant(bureau: IBureau, tenant: IBureauTenant): void {
        this.meurhBureausService
            .deleteTenant(bureau.bureau, tenant.bureautenant)
            .then(() => {
                this.NewToaster.pop({
                    type: 'success',
                    title: 'Tenant removido do bureau com sucesso!'
                });

                this.recarregarTenants(bureau);
            })
            .catch((error) => {
                this.NewToaster.pop({
                    type: 'error',
                    title: 'Erro ao remover tenant',
                    body: error?.data?.message || 'Ocorreu um erro ao remover o tenant do bureau.'
                });
            });
    }

    private executarRemocaoMembro(bureau: IBureau, membro: IBureauMembro): void {
        this.meurhBureausService
            .deleteMembro(bureau.bureau, membro.bureaumembro)
            .then(() => {
                this.NewToaster.pop({
                    type: 'success',
                    title: 'Membro removido do bureau com sucesso!'
                });

                this.recarregarMembros(bureau);
            })
            .catch((error) => {
                this.NewToaster.pop({
                    type: 'error',
                    title: 'Erro ao remover membro',
                    body: error?.data?.message || 'Ocorreu um erro ao remover o membro do bureau.'
                });
            });
    }

    private recarregarTenants(bureau: IBureau): void {
        const detalhe = this.detalhe(bureau);
        detalhe.tenantsBusy = true;

        this.meurhBureausService
            .getTenants(bureau.bureau)
            .then((tenants) => {
                detalhe.tenants = tenants || [];
                detalhe.tenantsCarregados = true;
            })
            .catch((error) => {
                this.NewToaster.pop({
                    type: 'error',
                    title: 'Erro ao carregar tenants',
                    body: error?.data?.message || 'Ocorreu um erro ao carregar os tenants do bureau.'
                });
            })
            .finally(() => {
                detalhe.tenantsBusy = false;
            });
    }

    private recarregarMembros(bureau: IBureau): void {
        const detalhe = this.detalhe(bureau);
        detalhe.membrosBusy = true;

        this.meurhBureausService
            .getMembros(bureau.bureau)
            .then((membros) => {
                detalhe.membros = membros || [];
                detalhe.membrosCarregados = true;
            })
            .catch((error) => {
                this.NewToaster.pop({
                    type: 'error',
                    title: 'Erro ao carregar membros',
                    body: error?.data?.message || 'Ocorreu um erro ao carregar os membros do bureau.'
                });
            })
            .finally(() => {
                detalhe.membrosBusy = false;
            });
    }

    /** O backend valida `tenant_id` com `is_int()`, então só um inteiro positivo pode ser enviado. */
    private tenantIdInformado(bureau: IBureau): number | null {
        const valor: any = this.detalhe(bureau).novoTenantId;

        if (valor === null || valor === undefined || valor === '') {
            return null;
        }

        const tenantId = parseInt(valor, 10);

        return Number.isInteger(tenantId) && tenantId > 0 ? tenantId : null;
    }

    private requisicaoCancelada(error: any): boolean {
        return error?.xhrStatus === 'abort';
    }
}
